/* eslint-disable prettier/prettier */
import { Checkbox, FormControlLabel, Grid } from '@mui/material';
import { getIn } from 'formik';
import PropTypes from 'prop-types';
import React, { memo, useCallback, useMemo } from 'react';

import { DollarIcon } from 'assets/v2/icons';
import InputField from 'components/v2/InputField';
import useFormFieldValidator from 'utils/hooks/useFormFieldValidator';

/**
 * TripMealsForm
 *
 * A reusable form component for managing meal-related fields in trip forms
 * Included number of days, number of attendants, meal cost, and advanced payment.
 *
 * @param {Object} props
 * @param {Object} props.formik - Formik instance managing form state
 * @param {string} props.fieldPrefix - Formik path prefix for meals, e.g. 'ancillaryServices.meals' or 'itineraryForm.legs[0].meals'
 * @param {boolean} props.isLoading - Flag to disable inputs while loading
 * @param {Object} [props.schema] - Optional Yup schema for automatically determining required fields
 * @param {string} [props.mealsAmountTitle='Cost of Meal Per Day'] - Label for the meals cost input
 * @param {string} [props.orgResourceId] - Optional orgResourceId to customize the label of Number of Attendants field
 * @param {boolean} [props.isRequired=true] - Whether the fields are required; controls validation
 */

const TripMealsForm = ({
  formik,
  fieldPrefix,
  isLoading,
  schema,
  mealsAmountTitle = 'Cost of Meal Per Day',
  orgResourceId,
  isRequired = true,
}) => {
  const { values, setFieldValue, setFieldTouched } = formik;

  const requiredFields = useMemo(
    () =>
      isRequired
        ? [
            `${fieldPrefix}.numberOfDays`,
            `${fieldPrefix}.numberOfAttendants`,
            `${fieldPrefix}.mealsCostInCents`,
        ]
        : [],
    [isRequired],
  );

  const fieldGroups = useMemo(
    () => [
      [
          `${fieldPrefix}.numberOfDays`,
          `${fieldPrefix}.numberOfAttendants`,
          `${fieldPrefix}.mealsCostInCents`,
          `${fieldPrefix}.advancedPaymentRequired`,
      ],
    ],
    [fieldPrefix],
  );

  const { fieldErrorMsg, fieldValidation, setUpdatedField } =
    useFormFieldValidator({
      formik,
      schema,
      requiredFields,
      fieldGroups,
    });

  const setValue = useCallback(
    async (key, value) => {
      const fullKey = `${fieldPrefix}.${key}`;
      await setFieldValue(fullKey, value);
      setFieldTouched(fullKey);
      setUpdatedField(fullKey);
    },
    [fieldPrefix, setFieldValue, setFieldTouched, setUpdatedField],
  );

  const handleNumberChange = useCallback(
    (key) =>
      ({ target: { value } }) => {
        const newValue = value === '' ? value : parseInt(value, 10).toString();
        setValue(key, newValue);
      },
    [setValue],
  );

  const handleAmountChange = useCallback(
    ({ target: { value } }) => {
      const roundedValue = Math.round(parseFloat(value) * 100);
      setValue(
        'mealsCostInCents',
        value === '' ? value : roundedValue.toString(),
      );
    },
    [setValue],
  );

  const handlePaymentChange = useCallback(
    ({ target: { checked } }) => setValue('advancedPaymentRequired', checked),
    [setValue],
  );

  const numberOfDays = useMemo(() => {
    const formValue = getIn(values, `${fieldPrefix}.numberOfDays`);
    if (!formValue && formValue !== 0) return '';
    return formValue;
  }, [values, fieldPrefix]);

  const numberOfAttendants = useMemo(() => {
    const formValue = getIn(values, `${fieldPrefix}.numberOfAttendants`);
    if (!formValue && formValue !== 0) return '';
    return formValue;
  }, [values, fieldPrefix]);

  const mealsAmount = useMemo(() => {
    const formValue = getIn(values, `${fieldPrefix}.mealsCostInCents`);
    if (formValue !== 0 && !formValue) return '';
    return formValue / 100;
  }, [values, fieldPrefix]);

  const advancedPayment = useMemo(
    () => getIn(values, `${fieldPrefix}.advancedPaymentRequired`) || false,
    [values, fieldPrefix],
  );

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} sm={6}>
        <InputField
          type="number"
          autoComplete="off"
          min={0}
          label="Number of Days"
          disabled={isLoading}
          required={isRequired}
          value={numberOfDays}
          onChange={handleNumberChange('numberOfDays')}
          valid={fieldValidation[`${fieldPrefix}.numberOfDays`]}
          error={fieldErrorMsg[`${fieldPrefix}.numberOfDays`]}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <InputField
          type="number"
          autoComplete="off"
          label={
            orgResourceId !== 'partnership'
              ? 'Number of Attendants'
              : 'Number of Attendant Days'
          }
          disabled={isLoading}
          required={isRequired}
          value={numberOfAttendants}
          onChange={handleNumberChange('numberOfAttendants')}
          valid={fieldValidation[`${fieldPrefix}.numberOfAttendants`]}
          error={fieldErrorMsg[`${fieldPrefix}.numberOfAttendants`]}
        />
      </Grid>
      <Grid item xs={12}>
        <InputField
          type="number"
          autoComplete="off"
          adornment={<DollarIcon />}
          min={0}
          label={mealsAmountTitle}
          disabled={isLoading}
          required={isRequired}
          value={mealsAmount}
          onChange={handleAmountChange}
          valid={fieldValidation[`${fieldPrefix}.mealsCostInCents`]}
          error={fieldErrorMsg[`${fieldPrefix}.mealsCostInCents`]}
        />
      </Grid>
      <Grid item xs={12}>
        <FormControlLabel
          checked={advancedPayment}
          control={<Checkbox />}
          disabled={isLoading}
          label="Advanced"
          sx={{ mt: -1, ml: -1 }}
          onChange={handlePaymentChange}
        />
      </Grid>
    </Grid>
  );
};

TripMealsForm.propTypes = {
  formik: PropTypes.object.isRequired,
  fieldPrefix: PropTypes.string.isRequired,
  isLoading: PropTypes.bool,
  mealsAmountTitle: PropTypes.string,
  orgResourceId: PropTypes.string,
  schema: PropTypes.object,
  isRequired: PropTypes.bool,
};

export default memo(TripMealsForm);
